// The one switch that decides whether customers hear from the assistant.
//
// Moved out of the Pilotage tab (Part B, 2026-09-25): switches live in
// Settings now, and the agent's state is also in the top bar of every tab.
//
// ⚠️ IT IS A SETTING, NOT CHAT WORK. The toggle asks `settings.agent_switch`
// (web/src/lib/actions.ts) — a manager who works the Inbox all day cannot
// silence the agent for every customer by accident.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { settingsApi } from '@/lib/api';
import { useT } from '@/lib/i18n';
import { useCan } from '@/lib/actions';

/**
 * The one switch that decides whether customers hear from the assistant.
 *
 * ⚠️ IT TELLS THE TRUTH ABOUT THE ALLOWLIST. "Sending" being ON while
 * `agent_test_numbers` holds two numbers does NOT mean the assistant is
 * answering customers — it means it is answering two phones. A big green
 * "actif" over that would be the most misleading thing on the page.
 */
export function MasterSwitch() {
  const { t } = useT();
  const qc = useQueryClient();
  const can = useCan();
  const allowed = can('settings.agent_switch');
  const { data } = useQuery({
    queryKey: ['app-settings', 'agent_test_numbers'],
    queryFn: () => settingsApi.get(),
  });
  const s = (data?.settings ?? {}) as Record<string, unknown>;
  const sending = s.assistant_send_enabled === true;
  const drafting = s.assistant_enabled === true;
  const allow = (s.agent_test_numbers as string[] | null) ?? [];
  // ⚠️ THE THIRD SWITCH, AND THE ONE THAT DECIDES *WHO*. `isAllowlistedPhone`
  // returns true for everybody when this is on, and the allowlist stops
  // mattering. Without reading it here the banner cannot tell "answering two
  // phones" from "answering the whole customer base".
  const unrestricted = s.agent_unrestricted_enabled === true;

  const mut = useMutation({
    mutationFn: (patch: Record<string, unknown>) => settingsApi.update(patch as never),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['app-settings', 'agent_test_numbers'] }),
  });

  // ⚠️ « AUCUNE LISTE DE TEST » MEANS NOBODY, NOT EVERYBODY, and this banner
  // said the opposite. `isAllowlistedPhone` refuses when the audience is `test`
  // and the list is EMPTY — `replyGate` records it as `allowlist_empty` — so an
  // empty list with the audience closed means not one customer hears anything.
  // The old `open` state rendered that as a green "Répond à TOUS les clients",
  // which is the most misleading thing this screen could say: an owner reading
  // it would believe the assistant was live and wonder why nobody replied.
  const state = !drafting ? 'off'
    : !sending ? 'draft'
    : unrestricted ? 'open'
    : allow.length ? 'limited'
    : 'nobody';

  const meta = {
    off:   { dot: 'bg-gray-400',  ring: 'border-gray-200 bg-white',
             title: t('Assistant éteint'), sub: t('Il ne prépare rien et n’envoie rien.') },
    draft: { dot: 'bg-amber-400', ring: 'border-amber-200 bg-amber-50/60',
             title: t('Prépare, n’envoie pas'), sub: t('Il rédige les réponses, vous les envoyez à la main.') },
    limited: { dot: 'bg-green-500', ring: 'border-green-200 bg-green-50/60',
             title: t('Répond — {n} numéro(s) autorisé(s)', { n: String(allow.length) }),
             sub: t('Les autres clients reçoivent une proposition à relire, pas un message.') },
    open:  { dot: 'bg-green-500', ring: 'border-green-300 bg-green-50',
             title: t('Répond à TOUS les clients'),
             sub: t('Tout le monde reçoit une réponse automatique, sans relecture.') },
    nobody: { dot: 'bg-amber-400', ring: 'border-amber-200 bg-amber-50/60',
             title: t('Personne ne reçoit de message'),
             sub: t('L’envoi est activé mais la liste de test est vide : chaque réponse attend un humain.') },
  }[state];

  return (
    <div className={`rounded-xl border px-4 py-3.5 flex items-center gap-3.5 flex-wrap transition-colors duration-200 storiz-fade-in ${meta.ring}`}>
      <span className={`w-2.5 h-2.5 rounded-full shrink-0 ring-4 ring-current/10 ${meta.dot}`} />
      <div className="min-w-0 flex-1">
        <p className="text-[13px] font-semibold text-gray-900 leading-tight">{meta.title}</p>
        <p className="text-[11.5px] text-gray-600 leading-snug mt-0.5">{meta.sub}</p>
      </div>
      <button
        type="button" role="switch" aria-checked={sending} disabled={mut.isPending || !allowed}
        title={allowed ? undefined : t('Réservé aux réglages')}
        aria-label={t('Activer les réponses automatiques')}
        onClick={() => {
          if (!sending && !allow.length && !window.confirm(t(
            'Activer les réponses automatiques ?\n\nAucun numéro de test n’est configuré : '
            + 'TOUS vos clients recevront des messages écrits par l’assistant, sans relecture.\n\nContinuer ?',
          ))) return;
          mut.mutate({ assistant_send_enabled: !sending });
        }}
        className={`relative w-[52px] h-[30px] rounded-full transition-colors duration-200 shrink-0 cursor-pointer disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 ${
          sending ? 'bg-green-600' : 'bg-gray-300'
        }`}
      >
        <span
          aria-hidden="true"
          className={`absolute left-0 top-[3px] w-6 h-6 rounded-full bg-white shadow-sm transition-transform duration-200 ease-[cubic-bezier(0.22,1,0.36,1)] ${
            sending ? 'translate-x-[25px]' : 'translate-x-[3px]'
          }`}
        />
      </button>
    </div>
  );
}
